"use client";

/**
 * `useForm` — a thin wrapper around `react-hook-form`'s `useForm` that
 * pre-wires the zod resolver and surfaces two convenience flags on top
 * of the raw rhf return:
 *
 * - `dirty` — `formState.isDirty`, hoisted to the top level so callers
 *   don't have to dig into `formState` to drive a "save" affordance.
 * - `submitting` — alias for `formState.isSubmitting`, same reason.
 *
 * Everything else is the unmodified rhf surface (register, handleSubmit,
 * setValue, reset, watch, getValues, formState…), so anything callers
 * know how to do with rhf they can do here too.
 *
 * Why we own this wrapper instead of having callers call rhf directly:
 *   1. The zod resolver wiring is otherwise repetitive at every form
 *      site.
 *   2. We may add platform-wide concerns (telemetry, dirty-confirm,
 *      autosave) inside the wrapper later without breaking call sites.
 *   3. We remap zod's root-path errors so they survive rhf's submit
 *      flow — see {@link FORM_ERROR_KEY} below.
 *
 * **Form-level error remapping (Task 15).** rhf 7.75's `handleSubmit`
 * deletes `errors.root` after the resolver runs but before the validity
 * check (see `Z(m.errors, "root")` in rhf's source) — so any error a zod
 * `.refine()` produces with `path: ["root"]` is silently dropped before
 * the form re-renders. To preserve cross-field errors (the canonical
 * use case for `.refine()`), the wrapper installs a resolver shim that:
 *
 *  1. Calls the original `zodResolver`.
 *  2. If the result has any error keyed under `root` (top level OR
 *     nested), copies that error onto a stable synthetic key
 *     `__formError` and removes the original `root` entry from the map.
 *  3. Returns the rewritten map to rhf.
 *
 * Callers who want a form-level error from a zod refinement either:
 *
 *  - Use `path: ["root"]` and let the wrapper remap (the natural
 *    convention from zod docs), OR
 *  - Use `path: [FORM_ERROR_KEY]` directly to skip the remap.
 *
 * The {@link FormErrorBanner} reads `formState.errors[FORM_ERROR_KEY]`
 * via the `formErrorMessage` selector exported below.
 */

import { useForm as useRhfForm } from "react-hook-form";
import type {
  DefaultValues,
  FieldValues,
  Mode,
  ResolverResult,
  UseFormReturn,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { z } from "zod";

/**
 * Reserved error key used by the form engine to surface form-level
 * errors (zod refinements that span multiple fields). The
 * {@link FormErrorBanner} renders the message stored at this key in
 * `formState.errors`. Callers should not register a real field with
 * this name — it is reserved for the engine's banner slot.
 *
 * Why a stable string instead of a Symbol: rhf's error map is a plain
 * object keyed by field name strings; a Symbol would not survive its
 * internal serialisation paths.
 */
export const FORM_ERROR_KEY = "__formError";

/**
 * Caller-facing options. We intentionally do NOT expose every rhf knob
 * — the surface is curated to what the platform needs today. Add
 * properties here as use cases arise; do not punch through `...rest`
 * blind, since that bakes implicit behavior into call sites.
 */
export interface UseFormOptions<TValues extends FieldValues> {
  /** Zod schema used for validation. */
  schema: z.ZodType<TValues>;
  /**
   * Initial values. rhf treats this as the "pristine" snapshot for the
   * `isDirty` calculation. Callers that hydrate from autosave should
   * pass the hydrated values here on first render, not after mount.
   */
  defaultValues?: DefaultValues<TValues>;
  /**
   * When validation runs. Defaults to `"onBlur"` — fields validate when
   * users leave them, not on every keystroke (per UX guidelines).
   */
  mode?: Mode;
}

/**
 * Return value: the full rhf instance plus our two convenience flags.
 * We use intersection rather than re-declaring rhf's `UseFormReturn`
 * shape so we never drift from upstream.
 */
export type UseFormResult<TValues extends FieldValues> =
  UseFormReturn<TValues> & {
    /** Mirror of `formState.isDirty`; true once any tracked field changes. */
    dirty: boolean;
    /** Mirror of `formState.isSubmitting`; true while `handleSubmit`'s onValid is in flight. */
    submitting: boolean;
  };

/**
 * Internal helper: rewrite a resolver `errors` map so that any entry
 * keyed under `root` (or nested under `root.<x>`) is mirrored onto the
 * reserved {@link FORM_ERROR_KEY} and the original `root` key is
 * dropped. This is the workaround for rhf's submit-time strip of
 * `errors.root`. The wrapper is conservative: it only touches `root`
 * and the synthetic key, so any field-level errors pass through
 * untouched.
 *
 * Top-level `root` (set when the user writes `path: ["root"]`) yields
 * an error like `{message, type}` — we copy it directly.
 *
 * Nested `root.<x>` (set when the user writes `path: ["root", "x"]`)
 * yields an error like `{x: {message, type}}` — we collapse the first
 * nested entry's message and use it as the form-level message.
 */
function remapRootErrors(
  errors: Record<string, unknown>,
): Record<string, unknown> {
  const root = errors["root"];
  if (root === undefined || root === null) return errors;
  // Build a fresh object so we don't mutate the resolver's return.
  const rewritten: Record<string, unknown> = { ...errors };
  delete rewritten["root"];
  // Already-set form error wins over a refinement-derived one — callers
  // who explicitly placed something at FORM_ERROR_KEY know what they want.
  if (rewritten[FORM_ERROR_KEY] !== undefined) {
    return rewritten;
  }
  // Top-level `errors.root = {message, type}` — copy verbatim.
  if (typeof (root as { message?: unknown }).message === "string") {
    rewritten[FORM_ERROR_KEY] = root;
    return rewritten;
  }
  // Nested `errors.root = {firstKey: {message, type}}` — surface the
  // first nested entry. Multiple nested entries collapse to the first
  // one; callers who need finer control should not nest under root.
  const nested = root as Record<string, unknown>;
  for (const key of Object.keys(nested)) {
    const entry = nested[key];
    if (
      entry !== null &&
      typeof entry === "object" &&
      typeof (entry as { message?: unknown }).message === "string"
    ) {
      rewritten[FORM_ERROR_KEY] = entry;
      return rewritten;
    }
  }
  return rewritten;
}

/**
 * Wraps `react-hook-form`'s `useForm` with `zodResolver(schema)`, then
 * spreads the rhf return alongside the two derived flags.
 *
 * Implementation note for `exactOptionalPropertyTypes: true`: the rhf
 * options type is `Partial<{...}>`, which under exactOptional rejects
 * `undefined` values. We build the props object conditionally so omitted
 * caller options stay omitted.
 */
export function useForm<TValues extends FieldValues>(
  options: UseFormOptions<TValues>,
): UseFormResult<TValues> {
  const { schema, defaultValues, mode } = options;

  // The resolver type from @hookform/resolvers is generic across schemas;
  // the cast keeps strict TS happy without leaking the resolver type to
  // callers. See gotchas: zodResolver's parameter signature widens
  // beyond what UseFormProps['resolver'] accepts in some TS versions.
  const baseResolver = zodResolver(schema);
  const wrappedResolver = (async (values, context, opts) => {
    const result = (await baseResolver(
      values,
      context,
      opts,
    )) as ResolverResult<TValues>;
    if (
      result.errors &&
      typeof result.errors === "object" &&
      Object.keys(result.errors).length > 0
    ) {
      const rewritten = remapRootErrors(
        result.errors as unknown as Record<string, unknown>,
      );
      return {
        ...result,
        errors: rewritten as ResolverResult<TValues>["errors"],
      };
    }
    return result;
  }) as Parameters<typeof useRhfForm<TValues>>[0] extends infer P
    ? P extends { resolver?: infer R }
      ? R
      : never
    : never;

  const form = useRhfForm<TValues>({
    resolver: wrappedResolver,
    mode: mode ?? "onBlur",
    ...(defaultValues !== undefined ? { defaultValues } : {}),
  });

  return {
    ...form,
    dirty: form.formState.isDirty,
    submitting: form.formState.isSubmitting,
  };
}

/**
 * Read the form-level error message (if any) from an rhf
 * {@link UseFormResult.formState} `errors` map. Returns `undefined` when
 * the form has no form-level error. Field components / banner consumers
 * call this rather than reaching into `errors[FORM_ERROR_KEY]` directly,
 * so the reserved-key contract stays in one place.
 */
export function readFormErrorMessage(
  errors: Record<string, unknown>,
): string | undefined {
  const entry = errors[FORM_ERROR_KEY] as { message?: unknown } | undefined;
  if (entry && typeof entry.message === "string") {
    return entry.message;
  }
  return undefined;
}
